import { z } from 'zod';
import { FieldSchema, FieldSchemaZod } from './field.schema.zod';
import { FieldGroupCardValueSchemaZod, FieldGroupSchema, FieldGroupSchemaZod } from './field.group.schema.zod';
import { JsonValue, JsonValueZod } from './json.value.zod';
import { RepeatSchema, RepeatSchemaZod } from './repeat.schema.zod';
import { ValidatorRef, ValidatorRefZod } from './validator.ref.zod';

/**
 * Inline related-entity collection for a section (Flag, ContactLink, …).
 * Parallel to {@link RelationTabSchema}: `relation` is an ActivityEntityName string.
 * Not host form fields and not a board tab — dense host-scoped list/cards.
 */
export interface SectionRelatedSchema {
  /**
   * Which related entity this section lists — an `ActivityEntityName` value.
   * String for persisted-schema compatibility (same as relation tabs).
   */
  relation: string;
  /** Parent is the mounted surface record. */
  parent: 'host';
  /**
   * `cards` = host-scoped card list. `board` = inline entity-manifest relation board.
   * `dialog` = compact launcher row that opens the same relation board in a modal —
   * for narrow hosts (quick-view Detail panes) where an inline board has no room.
   */
  presentation?: 'cards' | 'board' | 'dialog';
  /**
   * Model path of an embedded array on the host record whose length is shown as the
   * launcher count (`dialog` presentation only). Omit to show no count.
   */
  countBind?: string;
  /**
   * `dialog` launcher asks the related entity for `total` (host as parent). Use when the
   * related rows are not an embedded array on the host. Ignored when `countBind` is set.
   */
  countFromRelation?: boolean;
  /**
   * `panel` (default) = bordered section chrome for Contacts-on-Details.
   * `plain` = no panel border — tab already frames the list.
   */
  chrome?: 'panel' | 'plain';
}

export const SectionRelatedSchemaZod: z.ZodType<SectionRelatedSchema> = z.object({
  relation: z.string().min(1),
  parent: z.literal('host'),
  presentation: z.enum(['cards', 'board', 'dialog']).optional(),
  countBind: z.string().min(1).optional(),
  countFromRelation: z.boolean().optional(),
  chrome: z.enum(['panel', 'plain']).optional(),
}).strict();

/** Native section presentations understood by `wb-section` and Surface Engine hosts. */
export const SectionLayout = {
  /** Aligns every field on shared label/value columns for compact summaries and property panels. */
  PropertyList: 'property-list',
  /**
   * Same two-track property rows as PropertyList, but the label track is max-content of the
   * widest label in THIS section — not a fixed ~184px column — so values share one start edge
   * without a global staircase. Opt-in; PropertyList remains the default.
   */
  PropertyFit: 'property-fit',
  /** Stacked label-above-editor rows with PropertyList hover chrome. Does not switch to horizontal columns. */
  PropertyStack: 'property-stack',
  /** Presents the first fields as borderless title and description content at the top of a Detail/Form surface. */
  DetailIntro: 'detail-intro',
  /** Renders long-form text at full width without normal field labels or clipped value presentation. */
  Prose: 'prose',
  /** Presents lookup values as a compact, wrapping row of tags while hiding normal field labels. */
  TagRow: 'tag-row',
  /** Makes a spacer section invisible so its host contributes vertical breathing room instead of a divider. */
  Gap: 'gap',
} as const;

/** One native section presentation key. Omit layout for the default field-grid presentation. */
export type SectionLayout = typeof SectionLayout[keyof typeof SectionLayout];

/** Runtime validation for native section presentation keys. */
export const SectionLayoutZod: z.ZodType<SectionLayout> = z.enum([SectionLayout.PropertyList, SectionLayout.PropertyFit, SectionLayout.PropertyStack, SectionLayout.DetailIntro, SectionLayout.Prose, SectionLayout.TagRow, SectionLayout.Gap]);

/**
 * One bind a collapsed section names in its header, so a closed section still says what it holds.
 * A true boolean reads as its field label; any other value as its option label, unless it equals
 * `default`. `labels` maps a value to a shorter header word where the option label is a sentence.
 */
export interface SectionCollapsedSummaryEntry {
  bind: string;
  default?: JsonValue;
  labels?: Record<string, string>;
}

// The bind half of a field-group card value: both are a header line reading one live model bind.
const SectionCollapsedSummaryEntryZod: z.ZodType<SectionCollapsedSummaryEntry> = FieldGroupCardValueSchemaZod.pick({ bind: true }).extend({
  default: JsonValueZod.optional(),
  labels: z.record(z.string(), z.string().min(1)).optional(),
}).strict();

/** @deprecated Use {@link SectionRelatedSchema}. */
export type SectionItemsSchema = SectionRelatedSchema & { entity?: string; policy?: string };

export type SectionSchema = {
  id: string;
  title?: string;
  description?: string;
  icon?: string;
  /** Native section presentation; omit for the default field-grid layout. */
  layout?: SectionLayout;
  columns?: number;
  sectionRenderer?: string;
  fields?: FieldSchema[];
  fieldGroups?: FieldGroupSchema[];
  /**
   * Host-scoped related-entity records (section relation), independent of host fields.
   * Authored via formTab.relationSection() or section.related().
   */
  related?: SectionRelatedSchema;
  // First-class owned-array repeats (table / section-block / cards).
  repeats?: RepeatSchema[];
  sections?: SectionSchema[];
  validators?: ValidatorRef[];
  collapsible?: boolean;
  collapsedByDefault?: boolean;
  /** Binds whose non-default values the header lists while the section is collapsed. */
  collapsedSummary?: SectionCollapsedSummaryEntry[];
  /** Allows normal field editing when this section is rendered in a display-only summary slot. */
  editable?: boolean;
  // When true, every field in this section renders in display-only mode and
  // does not keep its bind active for validation (plan §Validation: a model
  // field stays active while at least one editable visible instance for it
  // remains). Sidebar summary sections set this to true.
  readonly?: boolean;
  // When true, the section carries no fields and renders as vertical spacing /
  // a divider between sibling sections. The compiler still emits it (so hosts
  // can lay it out); hosts render an empty spacer block instead of section
  // chrome + fields.
  spacer?: boolean;
  // Consecutive sibling sections sharing the same group id render inside one
  // group wrapper, so hosts can give the group a tighter internal gap (e.g. a
  // field list reading as one 4px-rhythm block) while the container's own gap
  // separates groups. Spacing stays container-owned — no per-section margins.
  group?: string;
  // How many grid columns this section should occupy when a host lays sections
  // out in a grid. `'all'` = full width; a positive integer = that many
  // columns. ADVISORY: current hosts stack sections vertically, so this is
  // recorded on the render info and honoured only by grid-laying hosts. No
  // visual change beyond spacer today.
  span?: 'all' | number;
};

export const SectionSchemaZod: z.ZodType<SectionSchema> = z.lazy(() => z.object({
  id: z.string().min(1),
  title: z.string().optional(),
  description: z.string().optional(),
  icon: z.string().optional(),
  layout: SectionLayoutZod.optional(),
  columns: z.number().int().positive().optional(),
  sectionRenderer: z.string().optional(),
  fields: z.array(FieldSchemaZod).optional(),
  fieldGroups: z.array(FieldGroupSchemaZod).optional(),
  related: SectionRelatedSchemaZod.optional(),
  repeats: z.array(RepeatSchemaZod).optional(),
  sections: z.array(SectionSchemaZod).optional(),
  validators: z.array(ValidatorRefZod).optional(),
  collapsible: z.boolean().optional(),
  collapsedByDefault: z.boolean().optional(),
  collapsedSummary: z.array(SectionCollapsedSummaryEntryZod).optional(),
  editable: z.boolean().optional(),
  readonly: z.boolean().optional(),
  // Fieldless spacer/divider section — see SectionSchema.spacer above.
  spacer: z.boolean().optional(),
  // Sibling grouping for host layout — see SectionSchema.group above.
  group: z.string().optional(),
  // Grid column span (advisory) — see SectionSchema.span above. `'all'` for
  // full width, or a positive integer count.
  span: z.union([z.literal('all'), z.number().int().positive()]).optional(),
}).strict());
